import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactLoading from "react-loading";
import Breadcrumb from "../../../components/breadcrumb";
import Footer from "../../../components/footer";
import PageBackgroundPattern from "../../../components/PageBackgroundPattern";
import JugadorAveragesTable from "../components/JugadorAveragesTable";
import JugadorGameLogSection from "../components/JugadorGameLogSection";
import JugadorHeroSection from "../components/JugadorHeroSection";
import JugadorModeToggle from "../components/JugadorModeToggle";
import JugadorPositionsCard from "../components/JugadorPositionsCard";
import JugadorRecentGamesSection from "../components/JugadorRecentGamesSection";
import JugadorTotalsTable from "../components/JugadorTotalsTable";
import TecnicoTotalsTable from "../components/TecnicoTotalsTable";
import useJugadorData from "../hooks/useJugadorData";

const INITIAL_COLLAPSED_SECTIONS = {
    averages: false,
    totals: false,
    gameLog: false,
    recentGames: false,
    tecnicoTotals: false,
};

function Jugador() {
    const { nombreJugador = "Jugador" } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const gameLogAnchorRef = useRef(null);
    const {
        isLoading,
        isError,
        hasData,
        displayName,
        principalProfile,
        jugadorProfile,
        hasJugadorProfile,
        dtProfile,
        hasTecnicoProfile,
        imageUrl,
        highs,
        averages,
        totals,
        careerProm,
        careerTotals,
        dtTotals,
        dtCareerTotals,
        ultimosCinco,
        positionsRecords,
        positionsMaximos,
        selectedLeague,
        gameLog,
        isGameLogLoading,
        loadGameLog,
    } = useJugadorData(nombreJugador);
    const [viewMode, setViewMode] = useState("jugador");
    const [collapsedSections, setCollapsedSections] = useState(INITIAL_COLLAPSED_SECTIONS);
    const [isGameLogFocused, setIsGameLogFocused] = useState(false);

    useEffect(() => {
        // Check for explicit type parameter in URL
        const params = new URLSearchParams(location.search);
        const typeParam = params.get("tipo");

        // If type parameter is specified and valid, use it
        if (typeParam === "tecnico" && hasTecnicoProfile) {
            setViewMode("tecnico");
            return;
        }

        if (typeParam === "jugador" && hasJugadorProfile) {
            setViewMode("jugador");
            return;
        }

        // Otherwise fall back to original logic
        if (hasJugadorProfile && !hasTecnicoProfile) {
            setViewMode("jugador");
            return;
        }

        if (!hasJugadorProfile && hasTecnicoProfile) {
            setViewMode("tecnico");
            return;
        }

        setViewMode("jugador");
    }, [hasJugadorProfile, hasTecnicoProfile, nombreJugador, location.search]);

    useEffect(() => {
        loadGameLog("");
    }, [viewMode, loadGameLog]);

    const activeProfile = useMemo(() => {
        if (viewMode === "tecnico") return dtProfile || principalProfile;
        return jugadorProfile || principalProfile;
    }, [viewMode, dtProfile, jugadorProfile, principalProfile]);

    const isJugadorMode = viewMode === "jugador";
    const profileLabel = isJugadorMode ? "Jugador" : "Técnico";
    const breadcrumbSection = isJugadorMode
        ? { label: "Jugadores", to: "/perfiles/jugadores", isCurrent: false }
        : { label: "Técnicos", to: "/perfiles/tecnicos", isCurrent: false };

    const handleShare = async () => {
        const url = window.location.href;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: displayName,
                    text: `${displayName} - Aguada Stats`,
                    url,
                });
                return;
            } catch (error) {
            }
        }

        try {
            await navigator.clipboard.writeText(url);
        } catch (error) {
        }
    };

    const handleCompare = () => {
        const params = new URLSearchParams({
            leftName: displayName,
            leftRole: viewMode,
        });

        navigate(`/perfiles/comparar?${params.toString()}`);
    };

    useEffect(() => {
        document.title = displayName || "Jugador";
    }, [displayName]);

    useEffect(() => {
        if (!selectedLeague) return;

        const navbarOffset = 100;

        requestAnimationFrame(() => {
            const target = gameLogAnchorRef.current;
            if (!target) return;

            const targetY = target.getBoundingClientRect().top + window.scrollY - navbarOffset;

            window.scrollTo({
                top: Math.max(targetY, 0),
                behavior: "smooth",
            });
        });

        setIsGameLogFocused(true);
        const focusTimeout = window.setTimeout(() => {
            setIsGameLogFocused(false);
        }, 1400);

        return () => {
            window.clearTimeout(focusTimeout);
        };
    }, [selectedLeague]);

    const toggleSection = (sectionKey) => {
        setCollapsedSections((current) => ({
            ...current,
            [sectionKey]: !current[sectionKey],
        }));
    };

    const handleLeagueClick = (liga) => {
        if (liga) {
            setCollapsedSections((current) => ({
                ...current,
                gameLog: false,
            }));
        }

        loadGameLog(liga);
    };

    return (
        <div className="relative min-h-screen overflow-hidden bg-[#062a1a] pt-16">
            <PageBackgroundPattern />
            <main className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-10 pt-2 pb-8 md:pt-2 md:pb-10">
                <header className="mb-6 sm:mb-8">
                    <Breadcrumb
                        items={[
                            { label: "Inicio", to: "/" },
                            breadcrumbSection,
                            { label: displayName, isCurrent: true },
                        ]}
                        className="mb-4"
                    />

                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="min-w-0">
                            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white border-l-4 border-red-500 pl-4 break-words">
                                {displayName}
                            </h1>
                            <p className="mt-2 text-sm md:text-base font-semibold uppercase tracking-wider text-slate-300 pl-4">
                                {profileLabel}
                            </p>
                        </div>

                        <div className="flex items-center gap-2 shrink-0 pl-4 sm:pl-0">
                            <button
                                type="button"
                                onClick={handleCompare}
                                className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 bg-[#102216] px-3 text-white active:text-green-400 sm:hover:text-green-400 sm:hover:bg-[#102216] transition-colors"
                            >
                                <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <path d="M8 5H5a2 2 0 0 0-2 2v3" />
                                    <path d="M16 19h3a2 2 0 0 0 2-2v-3" />
                                    <path d="M16 5h3a2 2 0 0 1 2 2v3" />
                                    <path d="M8 19H5a2 2 0 0 1-2-2v-3" />
                                    <path d="M8 12h8" />
                                    <path d="m10 10-2 2 2 2" />
                                    <path d="m14 10 2 2-2 2" />
                                </svg>
                                <span className="text-xs font-bold uppercase tracking-wide">Comparar</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleShare}
                                className="inline-flex h-10 items-center gap-2 rounded-lg border border-white/10 bg-[#102216] px-3 text-white active:text-green-400 sm:hover:text-green-400 sm:hover:bg-[#102216] transition-colors"
                            >
                                <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                    <circle cx="18" cy="5" r="3" />
                                    <circle cx="6" cy="12" r="3" />
                                    <circle cx="18" cy="19" r="3" />
                                    <path d="m8.6 13.5 6.8 3.9" />
                                    <path d="m15.4 6.6-6.8 3.8" />
                                </svg>
                                <span className="text-xs font-bold uppercase tracking-wide">Compartir</span>
                            </button>
                        </div>
                    </div>
                </header>

                <JugadorModeToggle
                    mode={viewMode}
                    onChange={setViewMode}
                    showJugador={hasJugadorProfile}
                    showTecnico={hasTecnicoProfile}
                />

                {isLoading ? (
                    <div className="rounded-xl border border-white/10 bg-[#102216] p-10 mb-8">
                        <ReactLoading type="bars" color="#22c55e" width={80} className="text-center m-auto mb-10" />
                        <p className="text-center text-slate-200 text-lg font-semibold">Buscando jugador...</p>
                    </div>
                ) : null}

                {isError && !isLoading ? (
                    <div className="rounded-xl border border-red-400/30 bg-red-950/20 p-8 text-center text-red-200 font-medium mb-8">
                        No se pudo cargar el jugador en este momento.
                    </div>
                ) : null}

                {!isLoading && !isError && !hasData ? (
                    <div className="rounded-xl border border-white/10 bg-[#102216] p-8 text-center text-slate-200 font-medium mb-8">
                        No existe el jugador que buscas.
                    </div>
                ) : null}

                {!isLoading && !isError && hasData ? (
                    <>
                        <JugadorHeroSection
                            displayName={displayName}
                            principalProfile={activeProfile}
                            jugadorProfile={jugadorProfile}
                            dtProfile={dtProfile}
                            imageUrl={imageUrl}
                            careerProm={careerProm}
                            dtCareerTotals={dtCareerTotals}
                            highs={highs}
                            mode={viewMode}
                        />

                        {isJugadorMode ? (
                            <>
                                <JugadorPositionsCard positions={positionsRecords} />
                                <JugadorPositionsCard
                                    positions={positionsMaximos}
                                    title="Posiciones en Máximos de Partido"
                                    showAmount
                                />

                                <JugadorAveragesTable
                                    averages={averages}
                                    totals={totals}
                                    careerProm={careerProm}
                                    careerTotals={careerTotals}
                                    nacimiento={activeProfile?.nacimiento}
                                    onLeagueClick={handleLeagueClick}
                                    isCollapsed={collapsedSections.averages}
                                    onToggleCollapse={() => toggleSection("averages")}
                                />

                                <JugadorTotalsTable
                                    totals={totals}
                                    careerTotals={careerTotals}
                                    nacimiento={activeProfile?.nacimiento}
                                    onLeagueClick={handleLeagueClick}
                                    isCollapsed={collapsedSections.totals}
                                    onToggleCollapse={() => toggleSection("totals")}
                                />

                                <div
                                    ref={gameLogAnchorRef}
                                    className={`rounded-xl transition-all duration-500 ${
                                        isGameLogFocused
                                            ? "ring-2 ring-green-400/60 shadow-[0_0_0_6px_rgba(74,222,128,0.16)]"
                                            : "ring-0 shadow-none"
                                    }`}
                                >
                                    <JugadorGameLogSection
                                        selectedLeague={selectedLeague}
                                        isLoading={isGameLogLoading}
                                        gameLog={gameLog}
                                        onClose={() => handleLeagueClick("")}
                                        isCollapsed={collapsedSections.gameLog}
                                        onToggleCollapse={() => toggleSection("gameLog")}
                                    />
                                </div>

                                <JugadorRecentGamesSection
                                    games={ultimosCinco}
                                    isCollapsed={collapsedSections.recentGames}
                                    onToggleCollapse={() => toggleSection("recentGames")}
                                />
                            </>
                        ) : (
                            <>
                                <JugadorPositionsCard positions={positionsRecords} isTecnico />

                                <TecnicoTotalsTable
                                    totals={dtTotals}
                                    careerTotals={dtCareerTotals}
                                    nacimiento={activeProfile?.nacimiento}
                                    isCollapsed={collapsedSections.tecnicoTotals}
                                    onToggleCollapse={() => toggleSection("tecnicoTotals")}
                                />
                            </>
                        )}
                    </>
                ) : null}
            </main>

            <div className="relative z-10">
                <Footer />
            </div>
        </div>
    );
}

export default Jugador;
